import { EnvironmentController } from '@/commons/environment/EnvironmentController';
import { TweakPanel } from '@/commons/TweakPanel';
import { ENVIRONMENT_PRESETS } from '@/commons/environment/EnvironmentPreset';
import { Color } from 'three';
import type { FolderApi } from 'tweakpane';

interface EnvParam {
    value: boolean | number | Color;
}

function getEnvironmentTab() {
    const environmentTab = TweakPanel.getFolderOrTabByName('Environment');
    if (environmentTab) {
        return environmentTab;
    }
    TweakPanel.getNextTabApi()?.addPage({
        title: 'Environment',
    });
    return TweakPanel.getFolderOrTabByName('Environment');
}

export function addEnvironmentFolder(envController: EnvironmentController, options?: { text: string; value: string }[]) {
    let pane = getEnvironmentTab();
    if (!pane) {
        const tweackPane = TweakPanel.getFolderOrTabByName();
        if (!tweackPane) {
            return;
        }
        tweackPane.addTab({
            pages: [{ title: 'Environment' }],
        });
        pane = TweakPanel.getFolderOrTabByName('Environment');
    }

    pane
        ?.addBlade({
            view: 'list',
            label: 'Environment Presets',
            options: options || ENVIRONMENT_PRESETS,
            value: 'venice_sunset_1k.hdr',
        })
        // @ts-expect-error value is a string
        .on('change', (ev) => {
            envController.setEnvironmentMapPreset(ev.value as string);
            const foundChildren = TweakPanel.getChildrenByLabel(pane as FolderApi, 'Use Environment as Background');
            if (foundChildren) {
                // @ts-expect-error foundChildren is an array
                foundChildren.controller?.value?.setRawValue(environmentController.useBackgroundAsEnvironment);
            }
        });

    const ENVIRONMENT_PARAMS: Record<string, EnvParam> = {
        useEnvAsBackground: {
            value: envController.useBackgroundAsEnvironment,
        },
        backgroundColor: {
            value: envController.backgroundColor,
        },
    };
    pane
        ?.addBinding(envController.renderer, 'toneMappingExposure', {
            label: 'Tone mapping exposure',
            min: 0,
            max: 3,
            value: envController.renderer.toneMappingExposure,
        })
        .on('change', (ev) => {
            envController.setToneMappingExposure(ev.value);
        });
    pane
        ?.addBinding(envController.scene, 'backgroundIntensity', {
            label: 'Background Intensity',
            min: 0,
            max: 8,
            value: envController.scene.backgroundIntensity,
        })
        .on('change', (ev) => {
            envController.setBackgroundIntensity(ev.value);
        });
    pane
        ?.addBinding(envController.scene, 'backgroundBlurriness', {
            label: 'Background Blur',
            min: 0.0,
            max: 1.0,
            value: envController.scene.backgroundBlurriness,
        })
        .on('change', (ev) => {
            envController.setBackgroundBlurriness(ev.value);
        });

    pane
        ?.addBinding(ENVIRONMENT_PARAMS.useEnvAsBackground, 'value', {
            label: 'Use Environment as Background',
            value: envController.useBackgroundAsEnvironment,
        })
        .on('change', (ev) => {
            envController.setUseBackgroundAsEnvironment(ev.value as boolean);
        });
    pane?.addBinding(
        {
            color: {
                get r() {
                    return envController.backgroundColor.r * 255;
                },
                set r(value) {
                    envController.backgroundColor.r = value / 255;
                },
                get g() {
                    return envController.backgroundColor.g * 255;
                },
                set g(value) {
                    envController.backgroundColor.g = value / 255;
                },
                get b() {
                    return envController.backgroundColor.b * 255;
                },
                set b(value) {
                    envController.backgroundColor.b = value / 255;
                },
            },
        },
        'color',
        {
            label: 'Background Color',
            picker: 'inline',
            expanded: false,
        },
    );
}

export function removeEnvironmentFolder() {
    TweakPanel.removeFolderByName('Environment');
}
